/**
 * KAN-735: списание аккаунтов: таблица по последним 6 месяцам, список списанных
 * за выбранный месяц и средний срок жизни.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatMonthLabel, writeOffReasonLabel } from '@/lib/expenses';
import { formatRub } from '@/lib/money';
import type { ExpensesReport } from '@/types/expenses';

interface Props {
  writeOffs: ExpensesReport['write_offs'];
  avgLifetimeDays: number | null;
  monthLabel: string;
}

export function WriteOffsBlock({ writeOffs, avgLifetimeDays, monthLabel }: Props): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border bg-white">
        <Table aria-label="Списание по месяцам" data-testid="write-offs-by-month">
          <TableHeader>
            <TableRow>
              <TableHead>Месяц</TableHead>
              <TableHead className="text-right">Штук</TableHead>
              <TableHead className="text-right">Сумма</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {writeOffs.by_month.map((m) => (
              <TableRow key={m.month} data-testid={`write-off-month-${m.month}`}>
                <TableCell>{formatMonthLabel(m.month)}</TableCell>
                <TableCell className="text-right tabular-nums">{m.count}</TableCell>
                <TableCell className="text-right tabular-nums">{formatRub(m.cost_rub)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-gray-700" data-testid="write-offs-avg-lifetime">
        Средний срок жизни списанного аккаунта:{' '}
        <span className="font-medium">
          {avgLifetimeDays === null ? 'нет данных' : `${Math.round(avgLifetimeDays)} дн.`}
        </span>
      </p>

      <div>
        <h4 className="mb-2 text-sm font-medium text-gray-900">Списаны за {monthLabel}</h4>
        {writeOffs.items.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="write-offs-items-empty">
            За месяц списаний нет
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <Table aria-label="Списанные аккаунты" data-testid="write-offs-items">
              <TableHeader>
                <TableRow>
                  <TableHead>Метка</TableHead>
                  <TableHead>Телефон</TableHead>
                  <TableHead className="text-right">Цена</TableHead>
                  <TableHead>Куплен</TableHead>
                  <TableHead>Списан</TableHead>
                  <TableHead>Причина</TableHead>
                  <TableHead className="text-right">Прожил, дн.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {writeOffs.items.map((item, i) => (
                  <TableRow key={`${item.phone}-${i}`} data-testid={`write-off-item-${i}`}>
                    <TableCell className="font-medium">{item.label || '-'}</TableCell>
                    <TableCell className="text-xs">{item.phone || '-'}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRub(item.purchase_cost_rub)}
                    </TableCell>
                    <TableCell className="text-xs">{formatDate(item.purchased_at)}</TableCell>
                    <TableCell className="text-xs">{formatDate(item.written_off_at)}</TableCell>
                    <TableCell className="text-xs">{writeOffReasonLabel(item.reason)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.lifetime_days ?? '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
